import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import logger from 'ws-scripts/modules/logger';
import { generateSchema, SchemaRenderer } from 'wsm-schema';
import { useFlags, usePrefs, useRequestData } from 'wsm-common-data';
import clickpathGenerator from '../utils/clickpathGenerator';
import { updateSession as trackingAttributesGenerator } from '../utils/trackingAttributesGenerator';
import trackingAttributesInjector from '../utils/trackingAttributesInjector';
import getKeysForExtraInfo from '../utils/getKeysForExtraInfo';
import pushEvent from '../utils/pushEvent';
import { DEFAULT_AD_PARAMS } from '../utils/defaultAdParams';

// I think this is up here out of the widget component so it loads as fast as possible
// but I don't remember
if (typeof window !== 'undefined') {
	import('../utils/eventHandling/handleEvents.js').then((module) => {
		module.default(window);
	});
	window.DDC = window.DDC || {};
	window.DDC.WidgetData = window.DDC.WidgetData || {};
	window.DDC.WidgetData['ws-tracking'] =
		window.DDC.WidgetData['ws-tracking'] || {};
	// so that cms-web stuff can push through the pushEvent
	window.DDC.WidgetData['ws-tracking'].pushEvent = (event) =>
		pushEvent(window, {
			eventData: event.eventData,
			eventType: event.event
		});
	window.dispatchEvent(new Event('pushEventReady'));
}

const Widget = () => {
	const { dlItems, addItems, schemaConfig } = useSelector((state) => state);

	const {
		cookiesForExtraInfo,
		formChangeTypes,
		maxClickpathCount,
		paramsForExtraInfo
	} = usePrefs();
	const { locale } = useRequestData();
	const flags = useFlags();

	const schemas = useMemo(() => {
		// Check if schema is enabled for this siteId via feature flag
		// LaunchDarkly targeting rule evaluates siteId and returns boolean
		const isSchemaEnabled =
			flags['ws-tracking-schema-enabled-sites'] === true;

		if (!isSchemaEnabled) {
			logger.info('Schema disabled for siteId:', schemaConfig?.siteId);
			return [];
		}
		if (schemaConfig?.pageAlias === 'INDEX' && addItems) {
			return generateSchema(['AutoDealer'], addItems);
		}
		return [];
	}, [schemaConfig, addItems, flags]);

	if (typeof window !== 'undefined') {
		window.DDC.WidgetData = window.DDC.WidgetData || {};
		window.DDC.WidgetData['ws-tracking'] =
			window.DDC.WidgetData['ws-tracking'] || {};
		window.DDC.WidgetData['ws-tracking'].formChangeTypes =
			formChangeTypes?.split(' ');
		window.DDC.WidgetData['ws-tracking'].confirmationData = window.DDC
			.WidgetData['ws-tracking'].confirmationData || {
			form: { fields: {}, fieldsTrimmedLowerCase: {} }
		};
		window.DDC.WidgetData['ws-tracking'].flags = flags;
		// if there are dlItems then we are on a confirm page and want to grab values
		// out of the submitted form from the previous page and add them to the confirmation object
		if (dlItems && Object.entries(dlItems)?.length) {
			Object.entries(dlItems).forEach(([item, itemValue]) => {
				let valToUse = itemValue;
				if (itemValue === 'true') {
					valToUse = true;
				} else if (itemValue === 'false') {
					valToUse = false;
				}
				window.DDC.WidgetData[
					'ws-tracking'
				].confirmationData.form.fields[item.replace('dl.', '')] =
					valToUse;
			});
		}
		window.DDC.WidgetData['ws-tracking'].paramsForExtraInfo =
			paramsForExtraInfo;
		window.DDC.WidgetData['ws-tracking'].cookiesForExtraInfo =
			cookiesForExtraInfo;
		window.DDC.WidgetData['ws-tracking'].maxClickpathCount =
			maxClickpathCount;
	}

	useEffect(() => {
		if (typeof window !== 'undefined' && !window?.DDC.isComposer) {
			clickpathGenerator();
			const urlParams = window.DDC.getUrlParams() || {};
			trackingAttributesGenerator(urlParams);
			trackingAttributesInjector({ locale });
		}

		// Store ALL URL params as a single JSON object in sessionStorage.
		// This allows other widgets (e.g. ws-iframe) to read persisted params
		// via the `sessionStorageParams` pref without needing per-param config.
		const params = new URLSearchParams(window.location.search);
		if (params.toString()) {
			let existing = {};
			try {
				existing = JSON.parse(
					window.sessionStorage.getItem('ws-url-params') || '{}'
				);
			} catch (e) {
				// ignore corrupt data
			}
			const merged = { ...existing };
			params.forEach((value, key) => {
				merged[key] = value;
			});
			window.sessionStorage.setItem(
				'ws-url-params',
				JSON.stringify(merged)
			);
		}

		getKeysForExtraInfo(window, paramsForExtraInfo).forEach(
			(mappedParamName, paramName) => {
				const extraInfoParam = `extra-info-param-${mappedParamName}`;
				const paramValue = window.DDC.getUrlParams()[paramName];
				if (paramValue) {
					window.sessionStorage.setItem(extraInfoParam, paramValue);
				}
			}
		);

		// Default ad params that are always tracked regardless of preferences
		DEFAULT_AD_PARAMS.forEach((paramName) => {
			const paramValue = params.get(paramName);
			if (paramValue) {
				window.sessionStorage.setItem(
					`extra-info-param-${paramName}`,
					paramValue
				);
			}
		});

		if (flags['ws-tracking-params-for-extra-info']) {
			window?.newrelic?.addPageAction('wsTrackingPrefs', {
				wsTrackingParamsForExtraInfo: paramsForExtraInfo,
				wsTrackingCookiesForExtraInfo: cookiesForExtraInfo,
				wsTrackingFormChangeTypes: formChangeTypes,
				wsTrackingMaxClickpathCount: maxClickpathCount
			});
		}
	}, [
		cookiesForExtraInfo,
		flags,
		formChangeTypes,
		locale,
		maxClickpathCount,
		paramsForExtraInfo
	]);

	return (
		<React.Fragment>
			<SchemaRenderer schemas={schemas} />
		</React.Fragment>
	);
};

export default Widget;
